import React, { useEffect, useState } from "react"
import { useMatch, useNavigate, useSearchParams } from "react-router-dom"
import { Modal } from "ui/Modal"
import dayjs from 'dayjs'
import { Form, Button, Spin, Flex, Grid } from "antd"
import { CustomForm } from "ui/Form"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import { serialize } from "object-to-formdata"
import { saveClient, cleanClient, getGuideClient } from "store/clients"
import { pick } from "lodash"
import { routes } from "router"
import styles from "./ClientForm.module.css"
import warningIcon from "assets/icons/warningIcon.svg";
import ClientForm from "./ClientForm"
import { LeftOutlined, LoadingOutlined } from "@ant-design/icons"
import { toaster } from "components/common/Toast"

const ClientWindow = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const isNew = useMatch(routes.guideClientNewPath())
  const isShow = useMatch(routes.guideClientPath())
  const isEdit = useMatch(routes.guideClientEditPath())
  const isPersisted = !isNew && (isShow || isEdit)
  const clientId = isPersisted?.params?.id
  const [isVisible, setIsVisible] = useState(true)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const client = useSelector((state) => state.clients.client)
  const { useBreakpoint } = Grid;
  const [loading, setLoading] = useState(true);

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    if (clientId) {
      dispatch(getGuideClient(clientId))
    } else {
      dispatch(cleanClient())
    }

    return () => dispatch(cleanClient())
  }, [clientId])

  useEffect(() => {
    if (client?.id) {
      form.setFieldsValue({
        ...client.user,
        state: client.user.state || "",
        country: client.user.country || "",
        street_address: client.user.address_line_1 || "",
        zip_code: client.user.zip_code || "",
        birthday: client.user.birthday ? dayjs(client.user.birthday) : null,
        client_groups: client.client_groups || [],
      });
      setLoading(false);
    } else if (!clientId) {
      setLoading(false);
    }
  }, [client]);

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const invalidFields = form.getFieldsError().filter(({ errors }) => errors.length);
      if (invalidFields.length) {
        invalidFields.forEach(({ name }) => form.setFieldsTouched([name], true));
        return;
      }

      const formValues = await form.validateFields();
      if (formValues?.street_address) {
        formValues["address_line_1"] = formValues.street_address
      }
      setLoading(true);

      if (clientId) {
        formValues.id = client.user.id;
      }

      const values = {
        ...formValues,
        birthday: formValues.birthday ? formValues.birthday.format('YYYY-MM-DD') : null
      };

      const user_attributes = pick(values, [
        "id",
        "nickname",
        "avatar",
        "gender",
        "first_name",
        "last_name",
        "email",
        "phone_number",
        "birthday",
        "address_line_1",
        "address_line_2",
        "city",
        "state",
        "country",
        "zip_code",
      ]);

      const group_clients_attributes = values.client_groups?.map(group => ({
        group_id: group?.group_id || group?.id,
        ...(group?._destroy ? { _destroy: true } : {})
      })) || [];

      const guide_client = {
        cash_payment: values.cash_payment,
        group_clients_attributes,
        notes_attributes: values.notes ? values.notes.map(note => ({ content: note.content })) : [],
        user_attributes,
      };

      if (user_attributes.avatar && !(user_attributes.avatar instanceof File)) {
        delete user_attributes.avatar;
      }

      const formData = serialize({ guide_client }, { indices: true });

      const response = await dispatch(saveClient(clientId, formData));

      // Only close and navigate if the API call succeeded
      if (response) {
        setIsVisible(false);
        toaster.success(response?.message);
        navigate(routes.guideClientsPath({}, searchParams));
      } else {
        setLoading(false);
      }

    } catch (err) {
      setLoading(false);
      if (err?.data?.errors["phone_number"]) {
        toaster.error(err.data.errors["phone_number"])
      }
      else if (err?.data?.errors?.user[0]) {
        toaster.error(err.data.errors.user[0])
      }
    }
  };


  const handleCancel = () => {
    if (form.isFieldsTouched()) {
      setShowDiscardConfirm(true)
    } else {
      closeForm()
    }
  }

  const closeForm = () => {
    setIsVisible(false)
    navigate(routes.guideClientsPath({}, searchParams))
  }

  const handleDiscardConfirm = () => {
    form.resetFields()
    setShowDiscardConfirm(false)
    closeForm()
  }

  const handleDiscardCancel = () => {
    setShowDiscardConfirm(false)
  }

  let content;
  const loadingContent = loading ? (
    <Flex justify="center" align="center" className={`position-absolute ${styles.modalLoaderWrapper}`}>
      <Spin className="" indicator={<LoadingOutlined spin />} />
    </Flex>
  ) : null;
  if (isMobile) {
    content = (
      <CustomForm className={`${styles.clientCustomForm} position-relative`} form={form} layout="vertical">
        <h4 className={`${styles.pageHeading} fw-700 flex gap-m pb-l`}>
          <LeftOutlined width={5} className={styles.backIcon} onClick={handleCancel} /> {isEdit ? t("client.edit") : t("client.add")}
        </h4>
        <ClientForm form={form} />
        <div className="mb-xs">
          <Flex gap={10} className={styles.formModalFooter}>
            <Button key="cancel" variant="outlined" onClick={handleCancel} size="large">
              {t("global.cancel")}
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={handleSubmit}
              size="large"
              loading={loading}
            >
              {t("global.save")}
            </Button>
          </Flex>
        </div>
        {loadingContent}
      </CustomForm>
    );
  } else {
    content = (
      <Modal
        width={1000}
        title={isEdit ? t("client.edit") : t("client.add")}
        open={isVisible}
        onCancel={handleCancel}
        className={styles.formModalWrapper}
        footer={
          <Flex gap={10} className={styles.formModalFooter}>
            <Button key="cancel" color="primary" variant="outlined" onClick={handleCancel} size="large">
              {t("global.cancel")}
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={handleSubmit}
              size="large"
              loading={loading}
            >
              {t("global.save")}
            </Button>
          </Flex>
        }
      >
        <CustomForm form={form} layout="vertical">
          <ClientForm form={form} />
          {loadingContent}
        </CustomForm>
      </Modal>
    );
  }

  return (
    <>
      {content}

      {/* Discard Confirmation Modal */}
      <Modal
        open={showDiscardConfirm}
        onCancel={handleDiscardCancel}
        className={styles.discardAlertModal}
        footer={[
          <Button key={t("global.no")} color="primary" variant="outlined" onClick={handleDiscardCancel}>
            {t("global.no")}
          </Button>,
          <Button key={t("global.yes")} type="primary" danger onClick={handleDiscardConfirm}>
            {t("global.yes")}
          </Button>,
        ]}
        title={
          <div>
            <Flex justify="center" align="center" className={`mb-xl ${styles.warningIcon}`}>
              <img src={warningIcon} alt="warning" width="24" height="24" />
            </Flex>
            {t("Discard Client?")}
          </div>
        }
      >
        <p>{t("global.discard_changes")}</p>
      </Modal>
    </>
  );

}

export default ClientWindow